import { colors, elevation } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import { inject } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { IconType, LinkConfiguration } from '../../../clients/sites/model';
import { openLinkInNewTab, redirect } from '../../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../../hoc/withAnalytics';
import Store from '../../../store';
import UserStore from '../../../store/userStore';
import { getSessionSynchronisationUrl } from '../../../util/auth';
import { isNewTabClick } from '../../../util/event';
import { cardContainerStyle, iconContainerStyle } from '../style';

export type LinkCardProps = LinkConfiguration & {
  fullScreen?: boolean;
} & InjectedStoreProps;

interface InjectedStoreProps {
  userStore: UserStore;
}

const IconContainer = styled.div`
  ${iconContainerStyle};
  background-color: ${colors.N700};
  border: 0;
  color: #ffffff;

  ${elevation.e200};
  transition: box-shadow 0.25s;

  &:hover {
    ${elevation.e400};
    color: #ffffff;
  }
`;

const LinkWrapper = styled.a`
  button {
    ${cardContainerStyle};
    background-color: transparent;
    border: 0;

    &:focus,
    &:hover {
      ${IconContainer} {
        ${elevation.e500};
      }
    }
  }
  &:hover {
    text-decoration: none;
  }
`;

const Description = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${colors.N800};
`;

class LinkCard extends React.Component<LinkCardProps & AnalyticsConsumerProps> {
  render() {
    const { Icon, label, href } = this.props;
    return (
      <LinkWrapper href={href} onClick={this.onClick} tabIndex={-1}>
        <Tooltip tag="button" content={label} hideTooltipOnClick>
          <div>
            <LinkCardIcon icon={Icon} />
            <Description>{label}</Description>
          </div>
        </Tooltip>
      </LinkWrapper>
    );
  }

  private onClick = (event: React.MouseEvent<HTMLElement>) => {
    const {
      fullScreen,
      labelAnalyticKey,
      href,
      synchroniseSession,
      synchronisationApplication,
      userStore,
    } = this.props;
    const { email } = userStore;
    const isOpeningInNewTab = isNewTabClick(event);
    event.preventDefault();
    this.props.analytics.ui('linkCard', 'clicked', {
      fullScreen,
      name: labelAnalyticKey,
      url: href,
    });
    const url = synchroniseSession ? getSessionSynchronisationUrl(href, email, synchronisationApplication) : href;
    if (isOpeningInNewTab) {
      openLinkInNewTab(url);
    } else {
      redirect(url);
    }
  };
}

const LinkCardIcon = ({ icon: Icon }: { icon: IconType }) => (
  <IconContainer>
    <Icon size="xlarge" />
  </IconContainer>
);

export default inject(({ userStore }: Store) => ({ userStore }))(withAnalytics(LinkCard));
